import dayjs, { Dayjs } from 'dayjs'

export interface ProgressDateRange {
  start: Dayjs
  end: Dayjs
}

export interface MonthlyProgressCounts {
  activity: number
  practices: number
  sharedChallenges: number
}

export interface ChallengeInterval {
  startDate: string
  endDate: string
}

const POINTS_PER_LEVEL = 10

const createCalendarDate = (year: number, month: number, day: number) => {
  const parsedDate = dayjs(
    `${String(year).padStart(4, '0')}-${String(month).padStart(
      2,
      '0',
    )}-${String(day).padStart(2, '0')}`,
  )

  if (
    !parsedDate.isValid() ||
    parsedDate.year() !== year ||
    parsedDate.month() !== month - 1 ||
    parsedDate.date() !== day
  ) {
    return dayjs('')
  }

  return parsedDate.startOf('day')
}

export const parseChallengeDate = (value: string) => {
  const normalizedValue = value.trim()
  const yearFirst = normalizedValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)

  if (yearFirst) {
    return createCalendarDate(
      Number(yearFirst[1]),
      Number(yearFirst[2]),
      Number(yearFirst[3]),
    )
  }

  const dayFirst = normalizedValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)

  if (dayFirst) {
    return createCalendarDate(
      Number(dayFirst[3]),
      Number(dayFirst[2]),
      Number(dayFirst[1]),
    )
  }

  const parsedDate = dayjs(normalizedValue)
  return parsedDate.isValid() ? parsedDate.startOf('day') : dayjs('')
}

export const isWithinRange = (date: Dayjs, range: ProgressDateRange) =>
  date.isValid() &&
  date.valueOf() >= range.start.valueOf() &&
  date.valueOf() <= range.end.valueOf()

export const countEventsWithinRange = (
  dates: Dayjs[],
  range: ProgressDateRange,
) => dates.filter((date) => isWithinRange(date, range)).length

export const countUniqueDaysWithinRange = (
  dates: Dayjs[],
  range: ProgressDateRange,
) =>
  new Set(
    dates
      .filter((date) => isWithinRange(date, range))
      .map((date) => date.format('YYYY-MM-DD')),
  ).size

export const challengeOverlapsRange = (
  challenge: ChallengeInterval,
  range: ProgressDateRange,
) => {
  const startDate = parseChallengeDate(challenge.startDate)
  const endDate = parseChallengeDate(challenge.endDate)

  return (
    startDate.isValid() &&
    endDate.isValid() &&
    startDate.valueOf() <= endDate.valueOf() &&
    startDate.valueOf() <= range.end.valueOf() &&
    endDate.endOf('day').valueOf() >= range.start.valueOf()
  )
}

export const calculateMonthlyGamification = (
  currentMonth: MonthlyProgressCounts,
  previousMonth: MonthlyProgressCounts,
) => {
  const activityBonus = currentMonth.activity > previousMonth.activity ? 1 : 0
  const practicesBonus =
    currentMonth.practices > previousMonth.practices ? 1 : 0
  const points =
    currentMonth.activity +
    currentMonth.practices +
    currentMonth.sharedChallenges +
    activityBonus +
    practicesBonus
  const level = Math.floor(points / POINTS_PER_LEVEL) + 1
  const pointsInLevel = points % POINTS_PER_LEVEL

  return {
    activityBonus,
    level,
    points,
    pointsInLevel,
    pointsPerLevel: POINTS_PER_LEVEL,
    pointsToNextLevel: POINTS_PER_LEVEL - pointsInLevel,
    practicesBonus,
  }
}
